import { useState, useEffect, useCallback } from 'react';
import { createEngagementClient } from '../../../../src/services/client';
import { IS_FAVORITE_CONTENT } from '../engagementGql/favoriteContent';
import { addErrorInDatadogRum } from '../utils/utilities';

export type ContentType = 'workout' | 'program';

type CheckFavoriteContentParams = {
  contentId: string;
  contentType: ContentType;
};

/**
 * Hook to check if content (workout or program) is favorited
 * Uses the IS_FAVORITE_CONTENT API for a simple boolean check
 *
 * @param contentId - For workouts: "programName|workoutName", for programs: "programName"
 * @param contentType - Either 'workout' or 'program'
 */
const useCheckFavoriteContent = ({ contentId, contentType }: CheckFavoriteContentParams) => {
  const [loading, setLoading] = useState(false);
  const [isFavorited, setIsFavorited] = useState(false);

  const checkFavoriteStatus = useCallback(async () => {
    if (!contentId) {
      return;
    }

    setLoading(true);
    try {
      const { data } = await createEngagementClient.query({
        query: IS_FAVORITE_CONTENT,
        variables: {
          contentId,
          contentType,
        },
        fetchPolicy: 'network-only',
      });

      setIsFavorited(data?.isFavoriteContent || false);
    } catch (error) {
      addErrorInDatadogRum(error);
      setIsFavorited(false);
    } finally {
      setLoading(false);
    }
  }, [contentId, contentType]);

  useEffect(() => {
    checkFavoriteStatus();
  }, [checkFavoriteStatus]);

  return {
    isFavorited,
    loading,
    refetch: checkFavoriteStatus,
  };
};

export default useCheckFavoriteContent;
